import { Fragment, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Check, X, Layers, ChevronDown, ChevronRight, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CMS → Fields → Groups tab. Manages the custom field groups (sub-sections)
 * admins can file CMS fields under, alongside the built-in ones (Property,
 * Property Address, Price, ...). Any group — including built-in ones — can
 * be renamed; only custom groups can be deleted. Expanding any group
 * (built-in or custom) lists its fields with editable Label/Info/Visibility
 * and a "Move to" selector, so a field can be edited or reassigned without
 * switching to the Fields tab.
 */
export default function CmsFieldGroupsManager() {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState([]); // [{key, label}]
  const [fieldSettings, setFieldSettings] = useState({}); // key -> full settings doc
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [movingKey, setMovingKey] = useState(null);
  const [fieldSavingKey, setFieldSavingKey] = useState(null);
  const fieldSaveTimers = useRef({});

  const loadGroups = async () => {
    try {
      const resp = await axios.get(`${API}/admin/cms-field-groups`, { withCredentials: true });
      setGroups(resp.data?.groups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load groups");
    }
  };

  const loadFields = async () => {
    try {
      const [mapResp, settingsResp] = await Promise.all([
        axios.get(`${API}/admin/integrations/rightmove-mapping`, { withCredentials: true }),
        axios.get(`${API}/admin/cms-field-settings`, { withCredentials: true }),
      ]);
      setFields(mapResp.data?.cms_fields || []);
      setFieldSettings(settingsResp.data?.settings || {});
    } catch (e) {
      toast.error("Failed to load fields");
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadGroups(), loadFields()]);
      setLoading(false);
    })();
  }, []);

  const fieldsInGroup = (groupKey) =>
    fields.filter((f) => (fieldSettings[f.key]?.section || "property") === groupKey);

  const moveField = async (fieldKey, newSection) => {
    const cur = fieldSettings[fieldKey] || {};
    if (fieldSaveTimers.current[fieldKey]) {
      clearTimeout(fieldSaveTimers.current[fieldKey]);
      delete fieldSaveTimers.current[fieldKey];
    }
    setMovingKey(fieldKey);
    try {
      await axios.put(`${API}/admin/cms-field-settings/${encodeURIComponent(fieldKey)}`, {
        ...cur,
        section: newSection,
      }, { withCredentials: true });
      setFieldSettings((prev) => ({ ...prev, [fieldKey]: { ...cur, section: newSection } }));
      toast.success("Field moved");
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to move field");
    } finally {
      setMovingKey(null);
    }
  };

  // Edits a field's own settings (Label/Info/Visibility/Form) from inside the
  // expanded group panel. Debounced like the Fields tab so typing doesn't
  // fire a save on every keystroke.
  const editField = (fieldKey, patch) => {
    setFieldSettings((prev) => {
      const cur = prev[fieldKey] || { info: "", visible_in: "admin", visible: true, list_form: false, section: "property", display_order: 0, label: "" };
      const nextVal = { ...cur, ...patch };
      if (fieldSaveTimers.current[fieldKey]) clearTimeout(fieldSaveTimers.current[fieldKey]);
      fieldSaveTimers.current[fieldKey] = setTimeout(async () => {
        try {
          setFieldSavingKey(fieldKey);
          await axios.put(`${API}/admin/cms-field-settings/${encodeURIComponent(fieldKey)}`, nextVal, { withCredentials: true });
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to save field");
        } finally {
          setFieldSavingKey((k) => (k === fieldKey ? null : k));
        }
      }, 500);
      return { ...prev, [fieldKey]: nextVal };
    });
  };

  const createGroup = async (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    if (!label) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/cms-field-groups`, { label }, { withCredentials: true });
      setNewLabel("");
      toast.success(`Added group "${label}"`);
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add group");
    } finally {
      setCreating(false);
    }
  };

  const startRename = (g) => {
    setEditingKey(g.key);
    setEditingLabel(g.label);
  };

  const saveRename = async (key) => {
    const label = editingLabel.trim();
    if (!label) return;
    setSavingKey(key);
    try {
      await axios.put(`${API}/admin/cms-field-groups/${encodeURIComponent(key)}`, { label }, { withCredentials: true });
      setEditingKey(null);
      toast.success("Group renamed");
      await loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename group");
    } finally {
      setSavingKey(null);
    }
  };

  const handleRenameKeyDown = (e, key) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveRename(key);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditingKey(null);
    }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete "${g.label}"? Fields in it will move to "Other".`)) return;
    setDeletingKey(g.key);
    try {
      await axios.delete(`${API}/admin/cms-field-groups/${encodeURIComponent(g.key)}`, { withCredentials: true });
      toast.success(`Deleted "${g.label}"`);
      if (expandedKey === g.key) setExpandedKey(null);
      await Promise.all([loadGroups(), loadFields()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete group");
    } finally {
      setDeletingKey(null);
    }
  };

  const toggleHiddenFromForm = async (g) => {
    const next = !g.hidden_from_form;
    setGroups((prev) => prev.map((x) => (x.key === g.key ? { ...x, hidden_from_form: next } : x)));
    try {
      await axios.put(`${API}/admin/cms-field-groups/${encodeURIComponent(g.key)}`, { hidden_from_form: next }, { withCredentials: true });
      toast.success(next ? `"${g.label}" hidden from the capture form` : `"${g.label}" shown on the capture form again`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
      setGroups((prev) => prev.map((x) => (x.key === g.key ? { ...x, hidden_from_form: !next } : x)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading field groups…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cms-field-groups-manager">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Layers className="h-4 w-4" />
          Field Groups
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Custom groups (sub-sections) you can file CMS fields under on the{" "}
          <strong>Fields</strong> tab, alongside the built-in ones. Any group — including
          built-in ones — can be renamed; only custom groups can be deleted. Click a group
          to see its fields and move any of them to a different group right here.
        </p>
      </div>

      <form onSubmit={createGroup} className="flex items-center gap-2 max-w-md">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="New group name, e.g. Rightmove Extras"
          data-testid="cms-field-group-new-input"
        />
        <Button type="submit" size="sm" disabled={creating || !newLabel.trim()} data-testid="cms-field-group-add-button">
          {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" /> Add Group</>}
        </Button>
      </form>

      <div className="border border-border rounded overflow-hidden max-w-4xl">
        <table className="w-full text-sm" data-testid="cms-field-groups-table">
          <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 font-medium">Group</th>
              <th className="px-3 py-2 font-medium w-24 text-center">Fields</th>
              <th className="px-3 py-2 font-medium w-32 text-center">Capture Form</th>
              <th className="px-3 py-2 font-medium w-28 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => {
              const isExpanded = expandedKey === g.key;
              const groupFields = isExpanded ? fieldsInGroup(g.key) : [];
              return (
              <Fragment key={g.key}>
              <tr className="border-t border-border align-middle" data-testid={`cms-field-group-row-${g.key}`}>
                <td className="px-3 py-2">
                  {editingKey === g.key ? (
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={editingLabel}
                        onChange={(e) => setEditingLabel(e.target.value)}
                        onKeyDown={(e) => handleRenameKeyDown(e, g.key)}
                        className="h-7 text-xs"
                        autoFocus
                        data-testid={`cms-field-group-edit-input-${g.key}`}
                      />
                      <button
                        type="button"
                        onClick={() => saveRename(g.key)}
                        disabled={savingKey === g.key}
                        className="p-1.5 rounded hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        data-testid={`cms-field-group-save-${g.key}`}
                      >
                        {savingKey === g.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 text-emerald-600" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingKey(null)}
                        className="p-1.5 rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`cms-field-group-cancel-${g.key}`}
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setExpandedKey(isExpanded ? null : g.key)}
                      className="flex items-center gap-1.5 hover:text-primary"
                      data-testid={`cms-field-group-expand-${g.key}`}
                    >
                      {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      <span className="text-sm">{g.label}</span>
                      {g.is_builtin && (
                        <Badge variant="outline" className="text-[9px] bg-muted text-muted-foreground">Built-in</Badge>
                      )}
                    </button>
                  )}
                  <div className="text-[10px] text-muted-foreground font-mono mt-0.5 ml-5">{g.key}</div>
                </td>
                <td className="px-3 py-2 text-center text-xs text-muted-foreground">{g.field_count}</td>
                <td className="px-3 py-2 text-center">
                  <label
                    className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer"
                    title="Hide this whole section — and every field under it — from the public 'List your property' capture form"
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-amber-600"
                      checked={!!g.hidden_from_form}
                      onChange={() => toggleHiddenFromForm(g)}
                      data-testid={`cms-field-group-hide-form-${g.key}`}
                    />
                    {g.hidden_from_form ? (
                      <span className="inline-flex items-center gap-1 text-amber-700"><EyeOff className="h-3 w-3" /> Hidden</span>
                    ) : "Hide"}
                  </label>
                </td>
                <td className="px-3 py-2 text-right">
                  {editingKey !== g.key && (
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => startRename(g)}
                        title="Rename"
                        className="p-2 rounded hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`cms-field-group-rename-${g.key}`}
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground hover:text-primary" />
                      </button>
                      {!g.is_builtin && (
                        <button
                          type="button"
                          onClick={() => deleteGroup(g)}
                          disabled={deletingKey === g.key}
                          title="Delete"
                          className="p-2 rounded hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                          data-testid={`cms-field-group-delete-${g.key}`}
                        >
                          {deletingKey === g.key ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-red-600" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
              {isExpanded && (
                <tr className="border-t border-border bg-muted/20" data-testid={`cms-field-group-fields-${g.key}`}>
                  <td colSpan={4} className="px-3 py-2">
                    {groupFields.length === 0 ? (
                      <p className="text-xs text-muted-foreground pl-5">No fields in this group.</p>
                    ) : (
                      <div className="space-y-2 pl-5 max-h-96 overflow-y-auto">
                        {groupFields.map((f) => {
                          const fs = fieldSettings[f.key] || {};
                          return (
                          <div key={f.key} className="flex flex-wrap items-center gap-2 pb-2 border-b border-border/60 last:border-0" data-testid={`cms-field-group-field-row-${f.key}`}>
                            <span className="text-[10px] text-muted-foreground font-mono w-32 shrink-0 truncate" title={f.key}>{f.key}</span>
                            <Input
                              value={fs.label ?? ""}
                              onChange={(e) => editField(f.key, { label: e.target.value })}
                              placeholder={`Label (default: "${f.label}")`}
                              className="h-6 text-[11px] px-1.5 w-40"
                              data-testid={`cms-field-group-field-label-${f.key}`}
                            />
                            <Input
                              value={fs.info ?? ""}
                              onChange={(e) => editField(f.key, { info: e.target.value })}
                              placeholder="Help text…"
                              className="h-6 text-[11px] px-1.5 flex-1 min-w-[8rem]"
                              data-testid={`cms-field-group-field-info-${f.key}`}
                            />
                            <select
                              value={fs.visible_in || "admin"}
                              onChange={(e) => editField(f.key, { visible_in: e.target.value })}
                              className="h-6 text-[11px] border border-input rounded-sm bg-background px-1 shrink-0"
                              title="Visibility"
                              data-testid={`cms-field-group-field-visible-${f.key}`}
                            >
                              <option value="admin">Admin</option>
                              <option value="user">User</option>
                              <option value="both">Both</option>
                              <option value="hidden">Hidden</option>
                            </select>
                            <label className="flex items-center gap-1 text-[11px] text-muted-foreground shrink-0" title="Show on public capture form">
                              <input
                                type="checkbox"
                                checked={!!fs.list_form}
                                onChange={(e) => editField(f.key, { list_form: e.target.checked })}
                                data-testid={`cms-field-group-field-listform-${f.key}`}
                              />
                              Form
                            </label>
                            {fieldSavingKey === f.key && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                            <select
                              value={g.key}
                              onChange={(e) => moveField(f.key, e.target.value)}
                              disabled={movingKey === f.key}
                              className="h-6 text-[11px] border border-input rounded-sm bg-background px-1 shrink-0 ml-auto"
                              title="Move to a different group"
                              data-testid={`cms-field-group-move-select-${f.key}`}
                            >
                              {groups.map((og) => (
                                <option key={og.key} value={og.key}>{og.label}</option>
                              ))}
                            </select>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
            {groups.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-sm text-muted-foreground">
                  No groups yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
